/**
 * Admin → Roles. Lists every role with its tool-grant count and how many
 * users hold it. Click a row → `RoleEditDrawer` for the tool-grant matrix.
 */
import { useMemo, useState } from "react";

import { adminApi } from "../../api/admin";
import { Modal } from "../../components/Modal";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import { RoleEditDrawer } from "./RoleEditDrawer";

export function RolesPage() {
  const rolesQuery = adminApi.roles.list();
  const usersQuery = adminApi.users.list();
  const deleteMutation = adminApi.roles.delete();
  const toast = useToast();
  const confirm = useConfirm();

  const [openRoleId, setOpenRoleId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const userCountByRole = useMemo(() => {
    const map = new Map<string, number>();
    for (const u of usersQuery.data ?? []) {
      for (const r of u.role_names) {
        map.set(r, (map.get(r) ?? 0) + 1);
      }
    }
    return map;
  }, [usersQuery.data]);

  return (
    <>
      <PageHeader
        title="Roles"
        description="Roles are bundles of tool grants per workspace. Assign them to users on the Users tab."
        actions={
          <button
            type="button"
            className="btn btn-main"
            onClick={() => setCreateOpen(true)}
          >
            Add role
          </button>
        }
      />

      <QueryBoundary
        query={rolesQuery}
        empty={
          <div className="empty-state">
            <div className="title">No roles defined</div>
            <p className="hint">
              Create the first role above (e.g. "crm-admin", "crm-user").
            </p>
          </div>
        }
        isEmpty={(r) => r.length === 0}
      >
        {(roles) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Workspace</th>
                  <th>Description</th>
                  <th className="text-right">Tools</th>
                  <th className="text-right">Users</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr
                    key={role.id}
                    onClick={() => setOpenRoleId(role.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="font-heading font-semibold text-ink">
                        {role.name}
                      </span>
                    </td>
                    <td>
                      <span className="tag-chip">{role.workspace_slug}</span>
                    </td>
                    <td className="text-sm text-ink-medium">
                      {role.description || "—"}
                    </td>
                    <td className="text-right tnum text-ink">
                      {role.tool_names.length}
                    </td>
                    <td className="text-right tnum text-ink">
                      {userCountByRole.get(role.name) ?? 0}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenRoleId(role.id);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-danger"
                        disabled={deleteMutation.isPending}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const ok = await confirm({
                            title: `Delete role "${role.name}"?`,
                            body: "Any user holding it loses the grant immediately.",
                            confirmLabel: "Delete",
                            tone: "danger",
                          });
                          if (!ok) return;
                          deleteMutation.mutate(role.id, {
                            onSuccess: () => toast.ok(`Role "${role.name}" deleted`),
                            onError: (err) =>
                              toast.error(err),
                          });
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      <RoleEditDrawer
        roleId={openRoleId}
        onClose={() => setOpenRoleId(null)}
      />
      <RoleCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
}

function RoleCreateModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toolsQuery = adminApi.tools.list();
  const createMutation = adminApi.roles.create();
  const toast = useToast();

  const [name, setName] = useState("");
  const [workspace, setWorkspace] = useState("crm");
  const [description, setDescription] = useState("");
  const [tools, setTools] = useState<Set<string>>(() => new Set());

  const reset = () => {
    setName("");
    setWorkspace("crm");
    setDescription("");
    setTools(new Set());
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const allTools = toolsQuery.data ?? [];
  const workspaces = useMemo(() => {
    const s = new Set<string>();
    for (const t of allTools) for (const w of t.workspaces) s.add(w);
    if (s.size === 0) s.add("crm");
    return Array.from(s).sort();
  }, [allTools]);

  const toggle = (toolName: string) => {
    setTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) next.delete(toolName);
      else next.add(toolName);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    createMutation.mutate(
      {
        name: name.trim(),
        workspace_slug: workspace,
        description: description.trim() || null,
        tool_names: Array.from(tools),
      },
      {
        onSuccess: () => {
          toast.ok("Role created");
          handleClose();
        },
        onError: (e) => toast.error(e),
      },
    );
  };

  // Filter tool list to selected workspace.
  const visibleTools = allTools.filter((t) => t.workspaces.includes(workspace));

  return (
    <>
      <Modal open={open} onClose={handleClose} title="Add role" eyebrow="Admin">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="role-name">
              Name
            </label>
            <input
              id="role-name"
              className="input"
              placeholder="e.g. crm-admin, crm-user"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="role-workspace">
              Workspace
            </label>
            <select
              id="role-workspace"
              className="input"
              value={workspace}
              onChange={(e) => setWorkspace(e.target.value)}
            >
              {workspaces.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="role-description">
              Description
            </label>
            <input
              id="role-description"
              className="input"
              placeholder="(optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              Tool grants ({tools.size}/{visibleTools.length})
            </label>
            {visibleTools.length === 0 ? (
              <span className="text-sm text-ink-low">
                No tools registered for this workspace yet.
              </span>
            ) : (
              <div
                className="panel"
                style={{ padding: "0.75rem", maxHeight: 240, overflowY: "auto" }}
              >
                <div className="space-y-1">
                  {visibleTools.map((tool) => (
                    <label
                      key={tool.name}
                      className="flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={tools.has(tool.name)}
                        onChange={() => toggle(tool.name)}
                      />
                      <span className="font-mono">{tool.name}</span>
                      <span className="text-ink-low text-xs">
                        ({tool.category})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-rule">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-main"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating…" : "Create role"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

